import { PaperStatus } from "@/components/utils/PdfStatus";
import { BasicUser } from "./auth";

export type HighlightType = 'topic' | 'motivation' | 'method' | 'evidence' | 'result' | 'impact' | 'general';

export interface ReferenceCitation {
    index: number;
    text: string;
    paper_id?: string;
}

export interface PaperData {
    filename: string;
    file_url: string;
    authors: string[];
    title: string;
    abstract: string;
    publish_date: string;
    summary: string;
    summary_citations?: ReferenceCitation[];
    institutions: string[];
    tags?: PaperTag[];
    starter_questions: string[];
    is_public: boolean;
    share_id: string;
    status: PaperStatus;
    journal?: string;
    doi?: string;
    publisher?: string;
    zotero_synced?: boolean;
}

export interface SharedPaper {
    paper: PaperData;
    highlights: PaperHighlight[];
    annotations: PaperHighlightAnnotation[];
    owner: BasicUser;
}

export interface CitationArtifactData {
    paper_id: string;
    title?: string;
    authors?: string[];
    publish_date?: string;
    journal?: string;
    publisher?: string;
    doi?: string;
}

export interface CitationArtifact {
    kind: 'citation';
    paper_id: string;
    preferred_style: string; // canonical key, e.g. "APA"
    style_display: string;
    data: CitationArtifactData;
    method: string;
    missing_fields: string[];
    confidence?: number | null;
}

export interface ChartField {
    key: string;
    label: string;
    unit?: string | null;
}

export interface ChartPlan {
    title: string;
    chart_type: 'bar' | 'line' | 'scatter';
    x: ChartField;
    y: ChartField;
    series?: ChartField | null;
    fields: ChartField[];
    calculation?: { label: string; spec: string; inputs: string[] } | null;
}

export interface ChartValue {
    value: string;
    quote: string;
    line_number?: string | null;
    // What this paper measured the value in, as it stated it — which is often
    // not what the chart is drawn in.
    unit?: string | null;
    // The lambda the extractor proposed to take this paper's number into the
    // field's unit, run in the sandbox. `lambda v: v` when the paper already
    // reported in it; empty when it could not be expressed there at all, in
    // which case the point is excluded and `conversion_note` says why.
    conversion?: string | null;
    conversion_note?: string | null;
    // The plotted quantity: `value` parsed and put through `conversion`, so it
    // is in the field's unit. `value` stays as the paper wrote it; null when
    // the quoted text states no plottable quantity.
    number?: number | null;
}

export interface ChartArtifact {
    kind: 'chart';
    // Set once the artifact has been persisted, so a chat card can link to its
    // viewer page. Absent while a chart is still streaming.
    artifact_id?: string;
    plan: ChartPlan;
    records: Array<{
        // One plotted point, not one paper: a paper reporting several
        // benchmarks contributes several records.
        record_id: string;
        paper_id: string;
        paper_title: string;
        values: Record<string, ChartValue>;
        exclusion_reason?: string | null;
    }>;
    coverage: {
        searched_paper_ids: string[];
        included_paper_ids: string[];
        excluded: Record<string, string>;
    };
    // Several papers reported the same x, so the study is what tells the
    // points apart and becomes the series.
    series_by_paper?: boolean;
    computation?: { script?: string; warnings?: string[] } | null;
    warnings: string[];
    investigation_trace?: MessageTrace | null;
}

export type ChatArtifact = CitationArtifact | ChartArtifact;

// A chat-generated artifact surfaced at the project level (artifacts panel),
// with breadcrumbs back to the conversation/message that produced it.
export interface ProjectChatArtifact {
    id: string;
    kind: 'citation' | 'chart';
    payload: ChatArtifact;
    message_id?: string | null;
    conversation_id?: string | null;
    conversation_title?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface ChartGenerationJob {
    id: string;
    project_id: string;
    // The assistant turn that asked for this chart, when chat did. Null for
    // jobs raised from the artifacts composer, which has no conversation.
    message_id?: string | null;
    prompt?: string;
    status: JobStatus;
    status_message?: string | null;
    error_message?: string | null;
    trace?: MessageTrace | null;
    created_at?: string | null;
    updated_at?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    artifact_id?: string | null;
    artifact?: ChartArtifact | null;
}

export interface MessageTraceToolCall {
    name: string;
    args?: Record<string, unknown>;
}

export interface MessageTraceStep {
    kind: string;
    detail: string;
    data?: Record<string, unknown> | null;
}

export interface MessageTraceCitation {
    paper_id: string;
    method: string;
    preferred_style: string;
    steps: MessageTraceStep[];
}

export interface MessageTrace {
    // The live "thinking trace" — status messages shown during streaming.
    status_messages?: string[];
    tool_calls?: MessageTraceToolCall[];
    citations?: MessageTraceCitation[];
}

// Denormalized snapshot of an @-mention attached to a user message, frozen at
// send time so it renders even if the paper/project is later renamed/deleted.
export interface MessageScopeItem {
    kind: 'paper' | 'project' | 'highlight';
    id: string;
    title: string;
    // For highlight mentions: the parent paper, so the pill can link to it
    // and show the paper title on hover.
    paper_id?: string;
    paper_title?: string;
    // For highlight mentions: the annotations written on the highlight.
    annotations?: string[];
}

export interface ChatMessage {
    id?: string;
    role: 'user' | 'assistant';
    content: string;
    references?: Reference;
    // First-party artifacts (e.g. citations) produced for this turn. Set for
    // both freshly-streamed and persisted/reloaded messages.
    artifacts?: ChatArtifact[];
    // Charts this turn asked for. A chart takes minutes, so the turn is
    // answered long before one exists: these render as pending cards and become
    // artifacts when their job completes.
    chart_jobs?: ChartGenerationJob[];
    // Agent trajectory (tool calls + per-citation subagent steps) for this turn.
    trace?: MessageTrace;
    // @-mention context attached to this (user) turn.
    scope?: MessageScopeItem[];
}

// Position types for react-pdf-highlighter-extended
export interface ScaledRect {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    width: number;
    height: number;
    pageNumber: number;
}

export interface ScaledPosition {
    boundingRect: ScaledRect;
    rects: ScaledRect[];
    usePdfCoordinates?: boolean;
}

export type HighlightColor = 'yellow' | 'green' | 'blue' | 'pink' | 'purple';

export interface PaperHighlight {
    id?: string;
    raw_text: string;
    role: 'user' | 'assistant';
    start_offset?: number;
    end_offset?: number;
    page_number?: number;
    type?: HighlightType;
    position?: ScaledPosition;
    color?: HighlightColor;
}

export interface PaperHighlightAnnotation {
    id: string;
    highlight_id: string;
    paper_id: string;
    content: string;
    role: 'user' | 'assistant';
    created_at: string;
}

export interface Reference {
    citations: Citation[];
}

export interface Citation {
    key: string;
    paper_id?: string;
    reference: string;
}

export interface Conversation {
    id: string;
    title: string;
    updated_at: string;
    is_owner?: boolean;
    owner_picture?: string;
    owner_name?: string;
}


export interface OpenAlexPaper {
    id: string
    title: string
    doi?: string
    publication_year: number
    publication_date: string
    open_access?: {
        is_oa: boolean
        oa_status: string
        oa_url?: string
    }
    keywords?: Array<{
        display_name: string
        score?: number
    }>
    primary_location?: {
        is_oa: boolean
        landing_page_url: string
        pdf_url?: string
        source?: {
            id: string
            display_name: string
            type?: string
            host_organization?: string
        }
    }
    biblio?: {
        volume?: string
        issue?: string
        first_page?: string
        last_page?: string
    }
    authorships?: Array<{
        author?: {
            id: string
            orcid?: string
            display_name?: string
        }
        institutions?: {
            id: string
            type: string
            display_name: string
            ror?: string
        }[]
    }>
    topics?: Array<{
        display_name: string
        score?: number,
        subfield: {
            display_name: string
        },
        field: {
            display_name: string
        },
        domain: {
            display_name: string
        }
    }>
    cited_by_count?: number
    abstract?: string
}

export interface OpenAlexResponse {
    meta: {
        count: number
        page: number | null
        per_page: number
    },
    results: Array<OpenAlexPaper>
}

export interface OpenAlexMatchResponse {
    center: OpenAlexPaper;
    cites: OpenAlexResponse;
    cited_by: OpenAlexResponse;
}

export const JobStatus = {
    PENDING: 'pending',
    RUNNING: 'running',
    COMPLETED: 'completed',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
} as const;

export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export const SubscriptionStatus = {
    ACTIVE: 'active',
    CANCELED: 'canceled',
    PAST_DUE: 'past_due',
    INCOMPLETE: 'incomplete',
    TRIALING: 'trialing',
    UNPAID: 'unpaid',
} as const;

export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export interface UserSubscription {
    has_subscription: boolean;
    had_subscription: boolean;
    requires_payment_update: boolean;
    subscription: {
        status: SubscriptionStatus;
        interval: "month" | "year";
        current_period_end: string;
        current_period_start: string;
        cancel_at_period_end: boolean;
    };
    scheduled_change?: {
        new_interval: "month" | "year";
        effective_date: string;
    } | null;
}

export interface HighlightResult {
    id: string;
    raw_text: string;
    start_offset: number | null;
    end_offset: number | null;
    page_number: number | null;
    role: string;
    created_at: string;
    type?: HighlightType;
}

export interface AnnotationResult {
    id: string;
    content: string;
    role: string;
    created_at: string;
    highlight: HighlightResult;
}

export interface PaperResult {
    id: string;
    title: string | null;
    authors: string[] | null;
    abstract: string | null;
    status: string;
    publish_date: string | null;
    created_at: string;
    last_accessed_at: string;
    highlights: HighlightResult[];
    annotations: AnnotationResult[];
    preview_url: string | null;
}

export interface SearchResults {
    papers: PaperResult[];
    total_papers: number;
    total_highlights: number;
    total_annotations: number;
}

export interface PaperImage {
    paper_id: string;
    s3_object_key: string;
    image_url: string;
    format: string;
    size_bytes: number;
    width: number;
    height: number;
    page_number: number;
    image_index: number;
    caption: string | null;
}

export interface JobStatusResponse {
    job_id: string;
    status: JobStatus;
    title: string | null;
    started_at: string;
    created_at: string;
    completed_at: string | null;
}

export interface PaperUploadJobStatusResponse extends JobStatusResponse {
    paper_id: string | null;
    has_file_url: boolean;
    has_metadata: boolean;
    celery_progress_message: string | null;
}

export interface PaperTag {
    id: string;
    name: string;
    color: string;
}

export interface PaperItem {
    id: string
    title: string
    abstract?: string
    authors?: string[]
    institutions?: string[]
    summary?: string
    created_at?: string
    publish_date?: string
    status?: PaperStatus
    preview_url?: string
    file_url?: string
    size_in_kb?: number
    tags?: PaperTag[]
    is_owner?: boolean
    journal?: string
    doi?: string
    publisher?: string
}

export interface CreditUsage {
    used: number;
    remaining: number;
    total: number;
    usagePercentage: number;
    showWarning: boolean;
    isNearLimit: boolean;
    isCritical: boolean;
}

export interface AudioOverview {
    id: string;
    conversable_id: string;
    conversable_type: string;
    audio_url: string;
    transcript: string;
    title: string;
    citations: ReferenceCitation[];
    created_at: string;
    updated_at: string;
    job_id: string;
}

export interface AudioOverviewJob extends JobStatusResponse {
    id: string;
    conversable_id: string;
    conversable_type: string;
    status_message: string | null;
    updated_at?: string | null;
}

export interface Project {
    id: string;
    title: string;
    description: string;
    num_papers?: number;
    num_conversations?: number;
    num_audio_overviews?: number;
    num_data_tables?: number;
    created_at: string;
    updated_at: string;
    role?: ProjectRole;
    num_roles?: number;
}

export interface PdfUploadResponse {
    message: string;
    job_id: string;
    file_name?: string;
}

export interface MinimalJob {
    jobId: string;
    fileName: string;
}

export const ProjectRole = {
    Admin: 'admin',
    Editor: 'editor',
    Viewer: 'viewer',
} as const;

export type ProjectRole = (typeof ProjectRole)[keyof typeof ProjectRole];

export interface Collaborator {
    id: string;
    name: string;
    picture: string;
    email: string;
    role: ProjectRole;
}

export interface PendingInvite {
    id?: string;
    email: string;
    role: ProjectRole;
    invited_at: string;
}

export interface ProjectInvitation {
    id: string;
    project_id: string;
    project_name: string;
    invited_by: string;
    email: string;
    role: string;
    accepted_at?: string;
    invited_at: string;
}

export interface DataTableJob {
    id: string;
    project_id: string | null;
    columns: string[] | null;
    task_id: string | null;
    title: string | null;
    status: JobStatus;
    started_at: string | null;
    completed_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    error_message: string | null;
    result_id: string | null;
}

// Response from /api/projects/tables/{job_id} status endpoint
export interface DataTableJobStatusResponse extends JobStatusResponse {
    columns: string[] | null;
    task_id: string | null;
    error_message: string | null;
    celery_status: string | null;
    celery_progress_message: string | null;
    celery_error: string | null;
}

// One element of a list-valued cell, individually cited.
export interface CellEntry {
    value: string;
    key?: string | null;
    citations: ReferenceCitation[];
}

export interface DataTableCellValue {
    value: string;
    citations: ReferenceCitation[];
    // Present only on list-valued cells; `value` holds the joined display form.
    entries?: CellEntry[] | null;
}

// A column in a proposed data-table schema; list columns extract one cited
// entry per instance, computed columns are produced by the sandboxed compute
// agent after extraction.
export interface ProposedDataTableColumn {
    label: string;
    kind: 'primitive' | 'list' | 'computed';
    // For computed columns: natural-language description of the computation.
    spec: string;
    // For computed columns: labels of the proposed columns the computation reads.
    inputs: string[];
    // Where the papers ground this column, per the propose agent's investigation.
    evidence?: string;
}

export interface DataTableRow {
    id: string;
    paper_id: string;
    values: {
        [columnName: string]: DataTableCellValue;
    };
}

// Entry in a table's column plan. kind "computed" entries carry a
// natural-language spec + input column labels; kind "list" entries mark list
// columns.
export interface DataTablePlanColumn {
    label: string;
    kind?: 'computed' | 'list';
    spec?: string;
    inputs?: string[];
}

// Provenance of the compute agent's run: the exact table snapshot the script
// ran against, the script, and its output — backs the "view code" panel.
export interface ComputeProvenance {
    version?: number;
    specs?: { label: string; spec: string; inputs: string[] }[];
    inputs_snapshot?: unknown;
    script?: string;
    stdout?: string;
    warnings?: string[];
    attempts?: number;
}

export interface DataTableResult {
    success: boolean;
    title: string;
    columns: string[];
    column_plan?: DataTablePlanColumn[];
    compute_provenance?: ComputeProvenance | null;
    rows: DataTableRow[];
    row_failures: string[] | null;
    created_at: string | null;
}

export interface SubscriptionLimits {
    paper_uploads: number;
    knowledge_base_size: number;
    chat_credits_weekly: number;
    audio_overviews_weekly: number;
    data_tables_weekly: number;
    discover_searches_weekly: number;
    projects: number;
    project_papers: number;
    model: string[];
}

export interface SubscriptionUsage {
    paper_uploads: number;
    paper_uploads_remaining: number;
    knowledge_base_size: number;
    knowledge_base_size_remaining: number;
    chat_credits_used: number;
    chat_credits_remaining: number;
    audio_overviews_used: number;
    audio_overviews_remaining: number;
    projects: number;
    projects_remaining: number;
    data_tables_used: number;
    data_tables_remaining: number;
    discover_searches_used: number;
    discover_searches_remaining: number;
}

export interface SubscriptionData {
    self_hosted?: boolean;
    plan: 'basic' | 'researcher';
    limits: SubscriptionLimits;
    usage: SubscriptionUsage;
}

export interface UseSubscriptionReturn {
    subscription: SubscriptionData | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}
